import React, { CSSProperties, useMemo, useState } from 'react'
import { List, Modal, Row, Tag } from 'antd'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { IntegrationType, Request } from 'src/types'

import { ButtonTitled, Text, Tooltip, Empty } from 'src/components/Ant'
import { getHotelsHistory } from 'src/core/store/ducks/hotels/actions'
import { saveFullRequest } from 'src/core/store/ducks/requests/actions'
import { SelectedHotel } from 'src/components/atoms/use-get-selected-hotels'
import { useGetSelectedHotels } from 'src/components/atoms'
import { WrapCard } from 'src/views/Requests/views/Form/components/Steps/styles'
import CurrencyMask from 'src/components/Formik/InputCurrency/mask'
import UnifiedTag from 'src/views/Requests/components/Table/components/UnifiedTag'

import { WrapText, WarpInfo, OfflineOffer } from './styles'
import ModalFormEdit from '../modal-form'
import ModalHistory from '../modal-history'

import { HotelImageUrl } from '../utils'

type Props = {
  bodyStyle?: CSSProperties
  enableModal?: boolean
  handleDelete?(item: SelectedHotel): void
  request: Request
}

function SummaryCard({
  bodyStyle = {},
  enableModal = false,
  handleDelete,
  request
}: Props): JSX.Element {
  const { t } = useTranslation()
  const [visibleModalEdit, setVisibleModalEdit] = useState<boolean>(false)
  const [visibleModalHistory, setVisibleModalHistory] = useState<boolean>(false)
  const dispatch = useDispatch()

  const selectedHotels = useGetSelectedHotels(request)

  function confirmDelete(item: SelectedHotel) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('common.actions.delete.message', {
        number: `#${item.request_quote_hotel_room_hash}`
      }),
      onCancel() {},
      onOk: async () => {
        return new Promise(() => {
          dispatch(
            saveFullRequest(
              {
                ...request,
                request_tickets: request.request_tickets.map(ticketItem => ({
                  ...ticketItem,
                  active:
                    +ticketItem.request_id ===
                    item.request_quote_hotel_room_hash
                      ? false
                      : ticketItem.active
                }))
              },
              true
            )
          )
        })
      },
      title: t('common.actions.delete.title')
    })
  }

  const hasHistory = useMemo<boolean>(
    () =>
      typeof request?.request_id === 'number' && selectedHotels?.length >= 1,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request?.request_id, selectedHotels]
  )

  const ToolText = ({
    message,
    ...props
  }: {
    message: string
    strong?: boolean
  }) => (
    <Tooltip title={<>{message}</>}>
      <WrapText>
        <Text {...props}>{message}</Text>
      </WrapText>
    </Tooltip>
  )

  return (
    <>
      <WrapCard
        bodyStyle={bodyStyle}
        data-test-id="hotel-summary-card"
        title="web.services.hotels.list-title"
        extra={[
          hasHistory && (
            <ButtonTitled
              key="hotel-history"
              icon="history"
              onClick={() => {
                if (request?.request_id) {
                  dispatch(getHotelsHistory({ request_id: request.request_id }))
                }
                setVisibleModalHistory(true)
              }}
              size="default"
              tooltip="common.actions.historical"
            />
          ),
          ' ',
          ...(enableModal
            ? [
                <ButtonTitled
                  data-test-id="hotel-summary-add"
                  key="hotel-add"
                  icon="plus"
                  onClick={() => setVisibleModalEdit(true)}
                  size="default"
                  tooltip="common.actions.add"
                />
              ]
            : [])
        ]}
      >
        <List
          data-test-id="hotel-summary-list"
          dataSource={selectedHotels}
          itemLayout="horizontal"
          locale={{ emptyText: <Empty onlyText /> }}
          renderItem={(item, index) => (
            <List.Item
              actions={[
                ...(!!handleDelete || enableModal
                  ? [
                      <ButtonTitled
                        data-test-id={`hotel-summary-item-${index}`}
                        icon="delete"
                        onClick={
                          enableModal
                            ? () => confirmDelete(item)
                            : () => handleDelete(item)
                        }
                        shape="circle"
                        tooltip="common.actions.remove"
                        type="danger"
                      />
                    ]
                  : [])
              ]}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      height: 70,
                      justifyContent: 'center',
                      width: 70
                    }}
                  >
                    <HotelImageUrl
                      style={{ maxHeight: 70, maxWidth: 70 }}
                      url={item.hotel_image_url}
                    />
                  </div>
                }
                title={<ToolText message={item.brand_name} strong />}
                description={
                  <>
                    {!!item.status && <UnifiedTag status={item.status} />}
                    {!!item.eticket_code && (
                      <Row>
                        <Text colon title="web.services.hotels.form.emission" />
                        <Text>{item.eticket_code}</Text>
                      </Row>
                    )}
                    <Row>
                      <ToolText message={item.address} />
                    </Row>
                    <Row>
                      <WarpInfo>
                        <Text
                          title={JSON.stringify({
                            message: 'web.services.hotels.form.daily',
                            params: { count: item.daily }
                          })}
                        >
                          {' - '}
                        </Text>
                        <Text
                          title={JSON.stringify({
                            message: 'web.services.hotels.form.guest',
                            params: { count: item.guests.length }
                          })}
                        />
                        <ToolText message={item.category} />
                      </WarpInfo>
                    </Row>
                    <Row>
                      <Text
                        colon
                        strong
                        title="web.services.hotels.form.period.start-time"
                      />
                      <Text>{item.checkin_date_format}</Text>
                    </Row>
                    <Row>
                      <Text
                        colon
                        strong
                        title="web.services.hotels.form.period.end-time"
                      />
                      <Text>{item.checkout_date_format}</Text>
                    </Row>
                    {item.integration_type === IntegrationType.OFFLINE && (
                      <Row>
                        <OfflineOffer>
                          {t('web.services.hotels.offline-offer')}
                        </OfflineOffer>
                      </Row>
                    )}
                    <Row>
                      <Tag
                        color="green"
                        style={{
                          fontSize: 20,
                          padding: 4,
                          textAlign: 'center'
                        }}
                      >
                        {`BRL ${
                          CurrencyMask({
                            formatStr: true,
                            value: item.total_value
                          }).maskedValue
                        }`}
                      </Tag>
                    </Row>
                  </>
                }
              />
            </List.Item>
          )}
        />
      </WrapCard>
      {enableModal && (
        <ModalFormEdit
          request={request}
          toggle={() => {
            setVisibleModalEdit(false)
          }}
          visible={visibleModalEdit}
        />
      )}
      <ModalHistory
        toggle={() => {
          setVisibleModalHistory(false)
        }}
        visible={visibleModalHistory}
      />
    </>
  )
}

export default SummaryCard
